import { getTrackedSeconds, TimeEntry } from '@/utils/taskTime';

/** Datas "AAAA-MM-DD" viram meia-noite local (sem pular para o dia anterior por fuso) */
export function parseLocalDate(value?: string | null): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const MONTHS_FULL = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

export const APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  consulta: 'Consulta',
  atendimento: 'Atendimento',
  audiencia: 'Audiência',
  reuniao: 'Reunião',
  depoimento: 'Depoimento',
  pericia: 'Perícia',
  mediacao: 'Mediação',
  conciliacao: 'Conciliação',
  diligencia: 'Diligência',
  inspacao_judicial: 'Inspeção judicial',
  sessao_julgamento: 'Sessão de julgamento',
};

export interface OfficeMonth {
  key: string;
  label: string;
  processes: number;
  clients: number;
  appointments: number;
  workedSeconds: number;
  tasksCompleted: number;
  revenue: number;
  expenses: number;
}

export interface OfficeReport {
  totals: {
    newProcesses: number;
    newJudicial: number;
    newAdministrative: number;
    newClients: number;
    appointments: number;
    appointmentsDone: number;
    appointmentsCanceled: number;
    workedSeconds: number;
    tasksCompleted: number;
    tasksCreated: number;
  };
  months: OfficeMonth[];
  appointmentsByType: { name: string; value: number }[];
  topTasks: { title: string; seconds: number }[];
  newClientsList: { name: string; date: string }[];
}

interface Input {
  from?: Date;
  to?: Date;
  cases: any[];
  adminProcesses: any[];
  clients: any[];
  tasks: any[];
  appointments: any[];
  financial: any[];
}

const isCanceled = (status?: string) => /cancel/i.test(status || '');
const isDone = (status?: string) => /conclu|realiz/i.test(status || '');

export function buildOfficeReport({ from, to, cases, adminProcesses, clients, tasks, appointments, financial }: Input): OfficeReport {
  const now = new Date();
  const start = from
    ? new Date(from.getFullYear(), from.getMonth(), from.getDate())
    : new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const end = to ? new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999) : new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const inRange = (d: Date | null) => !!d && d >= start && d <= end;

  // Um mês por linha, mesmo sem movimento
  const months = new Map<string, OfficeMonth>();
  for (let d = new Date(start.getFullYear(), start.getMonth(), 1); d <= end; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
    months.set(monthKey(d), {
      key: monthKey(d),
      label: `${MONTHS[d.getMonth()]}/${String(d.getFullYear()).slice(2)}`,
      processes: 0, clients: 0, appointments: 0, workedSeconds: 0, tasksCompleted: 0, revenue: 0, expenses: 0,
    });
  }
  const bump = (d: Date, field: keyof Omit<OfficeMonth, 'key' | 'label'>, amount = 1) => {
    const m = months.get(monthKey(d));
    if (m) m[field] += amount;
  };

  const totals = {
    newProcesses: 0, newJudicial: 0, newAdministrative: 0, newClients: 0,
    appointments: 0, appointmentsDone: 0, appointmentsCanceled: 0,
    workedSeconds: 0, tasksCompleted: 0, tasksCreated: 0,
  };

  cases.forEach((c) => {
    const d = parseLocalDate(c.created_at);
    if (!inRange(d)) return;
    totals.newProcesses++;
    totals.newJudicial++;
    bump(d!, 'processes');
  });
  adminProcesses.forEach((p) => {
    const d = parseLocalDate(p.created_at);
    if (!inRange(d)) return;
    totals.newProcesses++;
    totals.newAdministrative++;
    bump(d!, 'processes');
  });

  const newClientsList: { name: string; date: string }[] = [];
  clients.forEach((c) => {
    const d = parseLocalDate(c.created_at);
    if (!inRange(d)) return;
    totals.newClients++;
    bump(d!, 'clients');
    newClientsList.push({ name: c.name || 'Sem nome', date: d!.toLocaleDateString('pt-BR') });
  });
  newClientsList.sort((a, b) => a.date.split('/').reverse().join('').localeCompare(b.date.split('/').reverse().join('')));

  const byType = new Map<string, number>();
  appointments.forEach((a) => {
    const d = parseLocalDate(a.appointment_date);
    if (!inRange(d)) return;
    if (isCanceled(a.status)) {
      totals.appointmentsCanceled++;
      return;
    }
    totals.appointments++;
    if (isDone(a.status)) totals.appointmentsDone++;
    bump(d!, 'appointments');
    const type = APPOINTMENT_TYPE_LABELS[a.appointment_type] || (a.appointment_type ? String(a.appointment_type) : 'Outros');
    byType.set(type, (byType.get(type) || 0) + 1);
  });

  // Tempo trabalhado: cada sessão do cronômetro conta no dia em que começou
  const taskSeconds: { title: string; seconds: number }[] = [];
  tasks.forEach((t) => {
    if (inRange(parseLocalDate(t.created_at))) totals.tasksCreated++;
    if (/conclu/i.test(t.status || '') && inRange(parseLocalDate(t.updated_at))) {
      totals.tasksCompleted++;
      bump(parseLocalDate(t.updated_at)!, 'tasksCompleted');
    }
    const entries = Array.isArray(t.time_entries) ? (t.time_entries as TimeEntry[]) : [];
    let seconds = 0;
    if (entries.length) {
      entries.forEach((e) => {
        const d = parseLocalDate(e.started_at || e.ended_at);
        if (!inRange(d)) return;
        const s = getTrackedSeconds({ time_entries: [e] });
        seconds += s;
        bump(d!, 'workedSeconds', s);
      });
    } else {
      const d = parseLocalDate(t.updated_at || t.created_at);
      if (inRange(d)) {
        seconds = getTrackedSeconds(t);
        if (seconds) bump(d!, 'workedSeconds', seconds);
      }
    }
    if (seconds > 0) {
      totals.workedSeconds += seconds;
      taskSeconds.push({ title: t.title || 'Tarefa sem título', seconds });
    }
  });

  financial.forEach((e) => {
    const d = parseLocalDate(e.payment_date);
    if (!inRange(d)) return;
    if (e.entry_type === 'receita') bump(d!, 'revenue', Number(e.amount) || 0);
    if (e.entry_type === 'despesa') bump(d!, 'expenses', Number(e.amount) || 0);
  });

  return {
    totals,
    months: [...months.values()],
    appointmentsByType: [...byType.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value),
    topTasks: taskSeconds.sort((a, b) => b.seconds - a.seconds).slice(0, 8),
    newClientsList,
  };
}

/** "Relatório mensal · outubro de 2026" quando o período é um único mês */
export function reportTitle(from?: Date, to?: Date) {
  if (from && to && from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth()) {
    return { title: 'Relatório Mensal', subtitle: `${MONTHS_FULL[from.getMonth()]} de ${from.getFullYear()}` };
  }
  return { title: 'Relatório Gerencial', subtitle: '' };
}

/** Ex.: 12h 30min · 45min · 0h */
export function formatHours(totalSeconds: number) {
  const minutes = Math.round(totalSeconds / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h && !m) return '0h';
  if (!h) return `${m}min`;
  return m ? `${h}h ${String(m).padStart(2, '0')}min` : `${h}h`;
}
